// components/SchedulingForm.jsx
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import { Schedule, AccessTime } from "@mui/icons-material";
import DaySelector from "./DaySelector";
import TimeSlots from "./TimeSlots";
import CustomCheckbox from "../../../../components/CustomForm/CustomCheckbox";
import CustomInput from "../../../../components/FormFields/CustomInput";

const SchedulingForm = ({
  formData,
  handleChange,
  handleCheckBox,
  selectedDays,
  setSelectedDays,
  customTimeData,
  setCustomTimeData,
  setFormData,
  calculateTotalWeeklyHours,
  setRemovedDays,
  weeklyAssignment,
  totalWeeklyHours,
  isTimeCritical,
  daysOfWeek,
}) => {
  const theme = useTheme();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    day: null,
    dayName: null,
    assignmentId: null,
  });

  const existingDays = weeklyAssignment?.map(job => ({
    day: job.weekly_day,
    id: job.id
  })) || [];

  const handleAddSlot = (day) => {
    const newSlot = {
      id: null,
      time_required_from: "",
      time_required_to: "",
    };
    
    const currentSlots = Array.isArray(customTimeData[day]) ? customTimeData[day] : [];
    const newData = {
      ...customTimeData,
      [day]: [...currentSlots, newSlot],
    };
    
    setCustomTimeData(newData);
    setFormData((prev) => ({ ...prev, weekly_schedule_data: newData }));
    calculateTotalWeeklyHours(newData);
  };

  const handleRemoveSlot = (day, index) => {
    const slots = Array.isArray(customTimeData[day]) ? customTimeData[day] : [];
    const slotToRemove = slots[index];
    
    if (slotToRemove?.id) {
      setRemovedDays((prev) => [...prev, { 
        day: day, 
        id: slotToRemove.id,
        type: 'slot' 
      }]);
    }
    
    const newSlots = slots.filter((_, i) => i !== index);
    const newData = {
      ...customTimeData,
      [day]: newSlots,
    };
    
    if (newSlots.length === 0) {
      delete newData[day];
      setSelectedDays(selectedDays.filter((d) => d !== day));
    }
    
    setCustomTimeData(newData);
    setFormData((prev) => ({ ...prev, weekly_schedule_data: newData }));
    calculateTotalWeeklyHours(newData);
  };

  const handleCustomTimeChange = (day, index, key, value) => {
    const slots = Array.isArray(customTimeData[day]) ? [...customTimeData[day]] : [];
    slots[index] = { ...slots[index], [key]: value };
    
    const newData = {
      ...customTimeData,
      [day]: slots,
    };
    
    setCustomTimeData(newData);
    setFormData((prev) => ({ ...prev, weekly_schedule_data: newData }));
    calculateTotalWeeklyHours(newData);
  };

  const handleDaySelection = (day) => {
    const isSelected = selectedDays.includes(day);
    const existingDay = existingDays.find(d => d.day === day);

    if (isSelected && existingDay) {
      setConfirmDialog({
        open: true,
        day: day,
        dayName: day,
        assignmentId: existingDay.id
      });
      return;
    }

    if (isSelected) {
      const updatedDays = selectedDays.filter((d) => d !== day);
      setSelectedDays(updatedDays);

      const newTimeData = { ...customTimeData };
      delete newTimeData[day];
      setCustomTimeData(newTimeData);
      setFormData((prev) => ({ ...prev, weekly_schedule_data: newTimeData }));
      calculateTotalWeeklyHours(newTimeData);
      
      if (existingDay) {
        setRemovedDays((prev) => [...prev, { day: day, id: existingDay.id }]);
      }
    } else {
      const newTimeData = { ...customTimeData };
      newTimeData[day] = [{ time_required_from: "", time_required_to: "" }];
      setCustomTimeData(newTimeData);
      setSelectedDays([...selectedDays, day]);
      setFormData((prev) => ({ ...prev, weekly_schedule_data: newTimeData }));
      calculateTotalWeeklyHours(newTimeData);
    }
  };

  const confirmRemoveDay = () => {
    const { day, assignmentId } = confirmDialog;

    const updatedDays = selectedDays.filter((d) => d !== day);
    setSelectedDays(updatedDays);

    const newTimeData = { ...customTimeData };
    delete newTimeData[day];
    setCustomTimeData(newTimeData);
    setFormData((prev) => ({ ...prev, weekly_schedule_data: newTimeData }));
    calculateTotalWeeklyHours(newTimeData);

    setRemovedDays((prev) => [...prev, { day: day, id: assignmentId }]);

    setConfirmDialog({ open: false, day: null, dayName: null, assignmentId: null });
  };

  const cancelRemoveDay = () => {
    setConfirmDialog({ open: false, day: null, dayName: null, assignmentId: null });
  };

  const ConfirmationDialog = () => (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={cancelRemoveDay}
    >
      <Paper
        sx={{
          p: 3,
          maxWidth: 420,
          width: "90%",
          borderRadius: 2,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Remove Day Confirmation
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Are you sure you want to unselect <strong>{confirmDialog.dayName}</strong>?
          <br />
          <span style={{ color: "#EF4444", fontWeight: 500 }}>
            ⚠️ This will permanently remove all scheduled assignments and jobs for this day.
          </span>
        </Typography>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            onClick={cancelRemoveDay}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={confirmRemoveDay}
            sx={{ textTransform: "none" }}
          >
            Yes, Remove
          </Button>
        </Stack>
      </Paper>
    </Box>
  );

  return (
    <>
      {confirmDialog.open && <ConfirmationDialog />}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          overflow: "hidden",
          width: "100%",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Schedule sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
            <Typography variant="subtitle2" fontWeight={700} color="#1A1A2E" fontSize="0.85rem">
              Scheduling
            </Typography>
            <Chip
              label={`${selectedDays.length} days`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTime sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary" fontSize="0.7rem">
              {totalWeeklyHours}
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <Box
            sx={{
              p: 1.5,
              bgcolor: alpha(theme.palette.background.default, 0.4),
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            }}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: "block", fontSize: "0.65rem", letterSpacing: "0.3px", textTransform: "uppercase" }}>
              Select Days
            </Typography>

            <DaySelector
              daysOfWeek={daysOfWeek}
              selectedDays={selectedDays}
              weeklyAssignment={weeklyAssignment}
              onDaySelect={handleDaySelection}
            />

            <TimeSlots
              selectedDays={selectedDays}
              customTimeData={customTimeData}
              onTimeChange={handleCustomTimeChange}
              onAddSlot={handleAddSlot}
              onRemoveSlot={handleRemoveSlot}
              weeklyAssignment={weeklyAssignment}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 1.5 }}>
            <CustomCheckbox
              label="Time Critical?"
              name="time_critical"
              checked={formData.time_critical || false}
              onClick={(c) => handleCheckBox("time_critical", c)}
            />
            {!isTimeCritical && (
              <Box sx={{ width: 100 }}>
                <CustomInput
                  label="Tol. (min)"
                  name="time_tolerance"
                  type="number"
                  value={formData.time_tolerance || 60}
                  onChange={handleChange}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default SchedulingForm;